<script lang="ts">
    import Card from '@tile/components/card/Card.svelte';
    import ColorThumbs from '@tile/components/color-thumbs/ColorThumbs.svelte';
    import DealBadge from '@tile/components/deal-badge/DealBadge.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import ProductBrand from '@tile/components/product-brand/ProductBrand.svelte';
    import ProductHeader from '@tile/components/product-header/ProductHeader.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import ProductLink from '@tile/components/product-link/ProductLink.svelte';
    import ProductTitle from '@tile/components/product-title/ProductTitle.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { shouldShowWishlistIcon } from '@tilelist/domain/feature-visibility/WishlistElement.ts';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import ProductImageDispatcher from '@tilelist/layouts/dispatchers/product-image-dispatcher/ProductImageDispatcher.svelte';
    import WishlistIcon from '@tilelist/layouts/experiments/e3803/wishlist-icon/WishlistIcon.svelte';
    import type { FashionLayout } from '@tilelist/layouts/fashion-layout/FashionLayout.ts';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: FashionLayout;
        aspectRatio?: ImageAspectRatio;
        bestVariationBenefit?: string;
    };

    let {
        layout,
        colors,
        sponsored,
        advertiserLegalName,
        funderLegalName,
        imageLoadingOptions,
        rule,
        aspectRatio = 'portrait',
        onRedirectToPdp,
        element = $bindable(null),
        bestVariationBenefit,
    }: Props = $props();

    const onex = injectWiring().onex;
    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const showWishlistIcon = $derived(shouldShowWishlistIcon(onex));
    const { variation, title: productTitle, link: rawLink } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);

    /*                                                                                           */
    /*                                                         */
    const hasColors = $derived(!!(colors && colors.length > 1));
</script>

<Card
    data-jarvis="reptile-tile-container"
    data-variation-id={variation.variationId}
    bind:element
    disableOverlayEffect
    fullHeight
    size="50"
>
    <!-- NOTE: This is used in the Card component and is passed through -->
    {#snippet primaryAction()}
        <ProductLink id={`link-${variation.variationId}`} {link} onClick={onRedirectToPdp} />
    {/snippet}

    <div class="reptile-fashion-layout__image">
        <ProductImageDispatcher
            images={layout.images}
            fallbackImage={layout.primaryImage}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            brand={productTitle.brand}
            title={productTitle.display}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            {aspectRatio}
        />
        <!-- DealBadge and SocialProofBadges are self-positioning atoms (own position: absolute),
             rendered as flat siblings here rather than nested inside ProductImage,
             so both stay independently clickable/composable (see README). -->
        {#if variation.deal}
            <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        {/if}
        <SocialProofBadges socialProof={variation.socialProof} />
        {#if showWishlistIcon}
            <WishlistIcon variationId={variation.variationId} multipleSizes={variation.multipleSizes} />
        {/if}
    </div>

    <div class="reptile-fashion-layout__content">
        <ProductHeader>
            <ProductBrand brand={productTitle.brand} />
            <ProductTitle name={productTitle.display} />
            {#if variation.radioEquipmentRegulation}
                <div data-secondary-action>
                    <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
                </div>
            {/if}
        </ProductHeader>

        {#if variation.energyLabels?.length}
            <div data-secondary-action>
                <EekLabel labels={variation.energyLabels} asRow />
            </div>
        {/if}

        <div class="reptile-fashion-layout__purchase-info">
            <PriceDispatcher
                variationId={variation.variationId}
                saleTags={variation.saleTags}
                price={variation.price}
                dealHighlight={variation.deal?.highlight}
                dealId={variation.deal?.dealId}
                {rule}
                {bestVariationBenefit}
            />

            <div class="reptile-fashion-layout__delivery-group">
                <OttoUp show={variation.ottoUp} />

                {#if hasColors || sponsored}
                    <div class="reptile-fashion-layout__row" class:reptile-fashion-layout__row--end={!hasColors}>
                        <ColorThumbs {colors} />
                        <div data-secondary-action>
                            {#if sponsored || false}<ofc-sponsored-tag-v1
                                    funder-legal-name={funderLegalName}
                                    advertiser-legal-name={advertiserLegalName}
                                ></ofc-sponsored-tag-v1>{/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</Card>

<JsonLd {variation} />

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@shared/styles/mixins' as reptileMixins;

    .reptile-fashion-layout {
        &__image {
            position: relative;
        }

        &__content,
        &__purchase-info {
            display: flex;
            flex-direction: column;
        }

        &__content {
            row-gap: tokens.$oc-semantic-spacing-absolute-100;
            padding: tokens.$oc-semantic-spacing-absolute-75;
        }

        &__purchase-info {
            row-gap: tokens.$oc-semantic-spacing-absolute-100;
        }

        &__delivery-group {
            display: flex;
            flex-direction: column;
            row-gap: tokens.$oc-semantic-spacing-absolute-50;
        }

        &__row {
            @include reptileMixins.row-space-between;

            gap: tokens.$oc-semantic-spacing-absolute-50;

            /*                                                                                   */
            /*                               */
            &--end {
                justify-content: flex-end;
            }
        }
    }
</style>
